/**
 * Profile Page (Web)
 * User profile management with edit functionality
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only handles UI.
 * All profile updates are handled by backend User Service.
 */

import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Paper,
  alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  VerifiedUser as VerifiedIcon,
} from '@mui/icons-material';
import { useAuth } from '@shared/features/auth';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { UserProfile } from '@shared/types/user';

export function ProfilePage() {
  const { user, authenticated, loading, error, fetchUserData } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: user?.email || '',
    username: '',
    // Address fields
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
  });
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!authenticated) return;
      
      setProfileLoading(true);
      try {
        const { api } = await import('@shared/api/client');
        const response = await api.getCurrentUserProfile();
        if (response.data?.data) {
          setUserProfile(response.data.data as any);
        }
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [authenticated, updateSuccess]);

  const handleEdit = () => {
    // Parse full_name into first_name and last_name if needed
    const nameParts = user?.full_name ? user.full_name.trim().split(/\s+/) : [];
    const firstName = user?.first_name || nameParts[0] || '';
    const lastName = user?.last_name || nameParts.slice(1).join(' ') || '';
    
    setFormData({
      first_name: firstName,
      last_name: lastName,
      email: user?.email || '',
      username: user?.username || '',
      // Address fields from profile or billing address
      street_address: userProfile?.address || user?.billing_address?.street || '',
      city: userProfile?.city || user?.billing_address?.city || '',
      state: user?.billing_address?.state || '',
      postal_code: user?.billing_address?.postal_code || '',
      country: userProfile?.country || user?.billing_address?.country || '',
      phone: userProfile?.phone || '',
    });
    setIsEditing(true);
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      const { api } = await import('@shared/api/client');
      
      // Update user with first_name, last_name, and username
      await api.updateUser(user?.firebase_uid || '', {
        first_name: formData.first_name.trim() || undefined,
        last_name: formData.last_name.trim() || undefined,
        username: formData.username.trim() || undefined,
      } as any);

      // Build profile update payload (only include non-empty values)
      const profileUpdate: any = {};
      if (formData.street_address.trim()) profileUpdate.address = formData.street_address.trim();
      if (formData.city.trim()) profileUpdate.city = formData.city.trim();
      if (formData.country.trim()) profileUpdate.country = formData.country.trim();
      if (formData.phone.trim()) profileUpdate.phone = formData.phone.trim();

      // Update user profile (address fields)
      if (Object.keys(profileUpdate).length > 0) {
        await api.updateCurrentUserProfile(profileUpdate);
      }

      // Build billing address update payload
      const billingAddressUpdate: any = {};
      if (formData.street_address.trim()) billingAddressUpdate.street = formData.street_address.trim();
      if (formData.city.trim()) billingAddressUpdate.city = formData.city.trim();
      if (formData.state.trim()) billingAddressUpdate.state = formData.state.trim();
      if (formData.postal_code.trim()) billingAddressUpdate.postal_code = formData.postal_code.trim();
      if (formData.country.trim()) billingAddressUpdate.country = formData.country.trim();

      // Update user billing address (state, postal_code)
      if (Object.keys(billingAddressUpdate).length > 0) {
        await api.updateUser(user?.firebase_uid || '', {
          billing_address: {
            ...(user?.billing_address || {}),
            ...billingAddressUpdate,
          },
        } as any);
      }

      setUpdateSuccess(true);
      setIsEditing(false);
      
      // Refresh user data and profile
      await fetchUserData();
      const profileResponse = await api.getCurrentUserProfile();
      if (profileResponse.data?.data) {
        setUserProfile(profileResponse.data.data as any);
      }
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 5000);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update profile');
    }
  };

  if (!authenticated) {
    navigate('/login');
    return null;
  }

  if (loading && !user) {
    return (
      <AuthenticatedLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  const formatCategory = () => {
    // Check if category name is available in users_metadata
    if (user?.users_metadata?.category_name) {
      return user.users_metadata.category_name;
    }
    // Fallback to category_id if name not available
    if (user?.category_id) {
      return user.category_id;
    }
    return 'Not set';
  };

  return (
    <AuthenticatedLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)',
            }}
            onClose={() => {
              // Clear error from auth state if possible
            }}
          >
            {error}
          </Alert>
        )}

        {updateError && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)',
            }}
            onClose={() => setUpdateError(null)}
          >
            {updateError}
          </Alert>
        )}

        {updateSuccess && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(46, 125, 50, 0.15)',
            }}
            onClose={() => setUpdateSuccess(false)}
          >
            Profile updated successfully!
          </Alert>
        )}

        {/* Action Buttons - Outside Cards */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {isEditing ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                startIcon={<CancelIcon />}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderColor: '#04377b',
                  color: '#04377b',
                  '&:hover': {
                    borderColor: '#0652a2',
                    backgroundColor: alpha('#04377b', 0.04),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={<SaveIcon />}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: 'linear-gradient(135deg, #04377b 0%, #0652a2 100%)',
                  boxShadow: '0 4px 12px rgba(4, 55, 123, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0652a2 0%, #04377b 100%)',
                    boxShadow: '0 6px 16px rgba(4, 55, 123, 0.4)',
                  },
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                background: 'linear-gradient(135deg, #04377b 0%, #0652a2 100%)',
                boxShadow: '0 4px 12px rgba(4, 55, 123, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0652a2 0%, #04377b 100%)',
                  boxShadow: '0 6px 16px rgba(4, 55, 123, 0.4)',
                },
              }}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Profile Card */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(4, 55, 123, 0.08)',
                  border: '1px solid',
                  borderColor: alpha('#04377b', 0.1),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(4, 55, 123, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Paper
                      sx={{
                        p: 0.75,
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #04377b 0%, #0652a2 100%)',
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                      }}
                    >
                      <PersonIcon sx={{ color: 'white', fontSize: 16 }} />
                    </Paper>
                    <Typography variant="h6" sx={{ color: '#04377b', fontWeight: 700, fontSize: '1rem' }}>
                      Profile
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, borderColor: alpha('#04377b', 0.1) }} />
                  
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <PersonIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField
                              label="First Name"
                              value={formData.first_name}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, first_name: e.target.value })}
                              fullWidth
                              size="small"
                              required
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  '&:hover fieldset': {
                                    borderColor: '#04377b',
                                  },
                                },
                              }}
                            />
                            <TextField
                              label="Last Name"
                              value={formData.last_name}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, last_name: e.target.value })}
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  '&:hover fieldset': {
                                    borderColor: '#04377b',
                                  },
                                },
                              }}
                            />
                          </Box>
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              Name
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {user?.full_name || 'Not set'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <EmailIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, email: e.target.value })}
                            fullWidth
                            size="small"
                            required
                            disabled
                            helperText="Email changes require verification"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              Email
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                                {user?.email || 'Not set'}
                              </Typography>
                              {user?.email_verified && (
                                <Chip 
                                  icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
                                  label="Verified" 
                                  size="small" 
                                  color="success" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': {
                                      color: 'inherit',
                                    },
                                  }} 
                                />
                              )}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <PersonIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <TextField
                            label="Username"
                            value={formData.username}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, username: e.target.value })}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover fieldset': {
                                  borderColor: '#04377b',
                                },
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              Username
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {user?.username || 'Not set'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {user?.created_at && (
                    <Box sx={{ 
                      mb: 1.5, 
                      p: 1, 
                      borderRadius: 2,
                      backgroundColor: alpha('#04377b', 0.02),
                      border: '1px solid',
                      borderColor: alpha('#04377b', 0.08),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#04377b', 0.04),
                        borderColor: alpha('#04377b', 0.15),
                      },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: 1.5,
                            backgroundColor: alpha('#04377b', 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                          }}
                        >
                          <CalendarIcon sx={{ color: '#04377b', fontSize: 14 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                            Member Since
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {user?.status && (
                    <Box sx={{ 
                      mb: 1.5, 
                      p: 1, 
                      borderRadius: 2,
                      backgroundColor: alpha('#04377b', 0.02),
                      border: '1px solid',
                      borderColor: alpha('#04377b', 0.08),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#04377b', 0.04),
                        borderColor: alpha('#04377b', 0.15),
                      },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: 1.5,
                            backgroundColor: alpha('#04377b', 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                          }}
                        >
                          <VerifiedIcon sx={{ color: '#04377b', fontSize: 14 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                            Status
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem', textTransform: 'capitalize' }}>
                            {user.status}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
              </CardContent>
            </Card>
          </Grid>

            {/* Address Card */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(4, 55, 123, 0.08)',
                  border: '1px solid',
                  borderColor: alpha('#04377b', 0.1),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(4, 55, 123, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Paper
                      sx={{
                        p: 0.75,
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #04377b 0%, #0652a2 100%)',
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                      }}
                    >
                      <LocationIcon sx={{ color: 'white', fontSize: 16 }} />
                    </Paper>
                    <Typography variant="h6" sx={{ color: '#04377b', fontWeight: 700, fontSize: '1rem' }}>
                      Address
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, borderColor: alpha('#04377b', 0.1) }} />

                  {/* Street Address */}
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <LocationIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <TextField
                            label="Street Address"
                            value={formData.street_address}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, street_address: e.target.value })}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover fieldset': {
                                  borderColor: '#04377b',
                                },
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              Street Address
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {userProfile?.address || user?.billing_address?.street || 'Not set'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* City & Postal Code */}
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <LocationIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField
                              label="City"
                              value={formData.city}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, city: e.target.value })}
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  '&:hover fieldset': {
                                    borderColor: '#04377b',
                                  },
                                },
                              }}
                            />
                            <TextField
                              label="Postal Code"
                              value={formData.postal_code}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, postal_code: e.target.value })}
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  '&:hover fieldset': {
                                    borderColor: '#04377b',
                                  },
                                },
                              }}
                            />
                          </Box>
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              City & Postal Code
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {(() => {
                                const city = userProfile?.city || user?.billing_address?.city;
                                const postalCode = user?.billing_address?.postal_code;
                                const parts = [city, postalCode].filter(Boolean);
                                return parts.length > 0 ? parts.join(', ') : 'Not set';
                              })()}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* State */}
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <LocationIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <TextField
                            label="State"
                            value={formData.state}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, state: e.target.value })}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover fieldset': {
                                  borderColor: '#04377b',
                                },
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              State
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {user?.billing_address?.state || 'Not set'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Country */}
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <LocationIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <TextField
                            label="Country"
                            value={formData.country}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, country: e.target.value })}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover fieldset': {
                                  borderColor: '#04377b',
                                },
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              Country
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {userProfile?.country || user?.billing_address?.country || 'Not set'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Phone */}
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          mt: isEditing ? 0.5 : 0,
                        }}
                      >
                        <PhoneIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {isEditing ? (
                          <TextField
                            label="Phone"
                            value={formData.phone}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value })}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                '&:hover fieldset': {
                                  borderColor: '#04377b',
                                },
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                              Phone
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                              {userProfile?.phone || 'Not set'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>

                {profileLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

            {/* Category Card */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(4, 55, 123, 0.08)',
                  border: '1px solid',
                  borderColor: alpha('#04377b', 0.1),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(4, 55, 123, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Paper
                      sx={{
                        p: 0.75,
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #04377b 0%, #0652a2 100%)',
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                      }}
                    >
                      <CategoryIcon sx={{ color: 'white', fontSize: 16 }} />
                    </Paper>
                    <Typography variant="h6" sx={{ color: '#04377b', fontWeight: 700, fontSize: '1rem' }}>
                      Category
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, borderColor: alpha('#04377b', 0.1) }} />

                  {/* User Category */}
                  <Box sx={{ 
                    mb: 1.5, 
                    p: 1, 
                    borderRadius: 2,
                    backgroundColor: alpha('#04377b', 0.02),
                    border: '1px solid',
                    borderColor: alpha('#04377b', 0.08),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha('#04377b', 0.04),
                      borderColor: alpha('#04377b', 0.15),
                    },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.5,
                          borderRadius: 1.5,
                          backgroundColor: alpha('#04377b', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                        }}
                      >
                        <CategoryIcon sx={{ color: '#04377b', fontSize: 14 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                          User Category
                        </Typography>
                        <Chip
                          label={formatCategory()}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: alpha('#04377b', 0.1),
                            color: '#04377b',
                            height: 28,
                            fontSize: '0.75rem',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Language */}
                  {userProfile?.language && (
                    <Box sx={{ 
                      mb: 1.5, 
                      p: 1, 
                      borderRadius: 2,
                      backgroundColor: alpha('#04377b', 0.02),
                      border: '1px solid',
                      borderColor: alpha('#04377b', 0.08),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#04377b', 0.04),
                        borderColor: alpha('#04377b', 0.15),
                      },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: 1.5,
                            backgroundColor: alpha('#04377b', 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                          }}
                        >
                          <LanguageIcon sx={{ color: '#04377b', fontSize: 14 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.7rem' }}>
                            Language
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#04377b', fontSize: '0.875rem' }}>
                            {userProfile.language.toUpperCase()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </AuthenticatedLayout>
  );
}

